import { useInfiniteQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { getAllFilteredUsers } from '../api/userAPI'

const PER_PAGE = 10

export const usePaginatedUsers = (search = '', { disablePaginationOnSearch = false } = {}) => {
    const trimmedSearch = search.trim()
    const isSearching = !!trimmedSearch
    const skipPagination = disablePaginationOnSearch && isSearching

    const fetchPage = useCallback(
        async ({ pageParam = 1 }) => {
            const params = {
                order: 'asc',
                sort: 'name_tx',
            }
            if (isSearching) {
                params['name_tx'] = `like:${trimmedSearch}`
            }

            if (skipPagination) {
                // Other /filter endpoints in this app (e.g. getCongregationForRelocate)
                // only recognize the string 'false' here, not a JSON boolean - the
                // filter POST body is parsed server-side as raw request params, where
                // pagination is compared against the literal string.
                params.pagination = 'false'
                const users = await getAllFilteredUsers(params)
                const list = Array.isArray(users) ? users : []
                return {
                    current_page: 1,
                    per_page: list.length,
                    total: list.length,
                    data: list.map((u) => ({
                        value: u.user_id,
                        label: u.name_tx,
                    })),
                }
            }

            params.page = pageParam
            params.per_page = PER_PAGE
            const result = await getAllFilteredUsers(params)
            return {
                current_page: result?.current_page,
                per_page: result?.per_page,
                total: result?.total,
                data: (result?.data || []).map((u) => ({
                    value: u.user_id,
                    label: u.name_tx,
                })),
            }
        },
        [trimmedSearch, isSearching, skipPagination]
    )

    return useInfiniteQuery({
        queryKey: ['paginated-users', search, skipPagination],
        queryFn: fetchPage,
        getNextPageParam: (lastPage) => {
            if (skipPagination) return undefined
            if (lastPage?.current_page * lastPage?.per_page < lastPage?.total) {
                return lastPage.current_page + 1
            }
        },
        staleTime: 0,
        cacheTime: 0,
        keepPreviousData: false,
    })
}
